import React, { useContext, useEffect, useState } from 'react';
import { Modal, Checkbox } from 'antd';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import { priceSimulatorDataContext } from 'contexts/PriceSimulatorContext';
import { CustomSelect } from 'CustomComponentCss';
import DownSvg from 'assets/svg/Down';
import Cross from 'assets/svg/Cross';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PSR_IDEN_OPPINPUT_TABLE_COPY_EVENT_TRIGGERED,
    PSR_IDEN_OPPINPUT_TABLE_COPY_EVENT_DROPDOWN_CHANGED,
} from 'utils/mixpanelEventNames';

type LinkScenarioModalProps = {
    visible: boolean;
    closeModal: () => void;
    linkTriggeredPPG: { label: string; value: number };
    setLinkPPGState: any;
};
const LinkScenarioModal = ({ visible, closeModal, linkTriggeredPPG, setLinkPPGState }: LinkScenarioModalProps) => {
    const { simulationInputDataState } = useContext(priceSimulatorDataContext);
    const [tempCheckedValues, setTempCheckedValues] = useState<string[]>([]);
    const [availablePPPGs, setAvailablePPGs] = useState<any>([]);
    const [selectedPPG, setSelectedPPG] = useState(linkTriggeredPPG);

    const onApplyChange = () => {
        if (tempCheckedValues.length)
            setLinkPPGState({ sourceState: selectedPPG.value, destinationPPGs: tempCheckedValues });
        trackEventMixpanel(PSR_IDEN_OPPINPUT_TABLE_COPY_EVENT_TRIGGERED);

        closeModal();
    };

    const onResetClick = () => {
        closeModal();
    };

    const handleCheckboxChange = (checkedList: any) => {
        setTempCheckedValues(checkedList);
    };

    useEffect(() => {
        setSelectedPPG(linkTriggeredPPG);
    }, [linkTriggeredPPG]);

    useEffect(() => {
        setTempCheckedValues([]);
        const ppgs: any = simulationInputDataState
            .filter((item: any) => item.nestedIndex === null)
            .filter((item: any) => item.label !== selectedPPG.label)
            .map((item: any) => {
                return {
                    label: item.label,
                    value: item.index,
                };
            });
        setAvailablePPGs(ppgs);
    }, [selectedPPG]);

    const handleRemoveSelection = (unselectedId: number) => {
        const newSelectedValue = tempCheckedValues.filter((id: any) => id !== unselectedId);
        setTempCheckedValues(newSelectedValue);
    };

    return (
        <StyledModal
            open={visible}
            title="Link Products"
            onCancel={closeModal}
            footer={null}
            width={550}
            className="rounded "
            centered
        >
            <LevelsFilterDiv>
                <span className="font-xs">Select PPG</span>
                <CustomSelect
                    suffixIcon={<DownSvg />}
                    className="dropdown-height w-48 uniqSelLSModelPPGDropdown"
                    value={selectedPPG}
                    options={availablePPPGs}
                    onChange={(val: any, item: any) => {
                        setSelectedPPG(item);
                        trackEventMixpanel(PSR_IDEN_OPPINPUT_TABLE_COPY_EVENT_DROPDOWN_CHANGED, {
                            selectedItem: item.label,
                        });
                    }}
                />
            </LevelsFilterDiv>

            <div className="border border-borderLinePurpuleAlt rounded overflow-hidden">
                <div className=" border-b border-borderLinePurpuleAlt p-2.5 bg-bgBackground font-bold text-purpleFont font-xs">
                    Select Linked PPG(s)
                </div>

                <Checkbox.Group className="w-full" value={tempCheckedValues} onChange={handleCheckboxChange}>
                    <div className="w-full overflow-y-scroll max-h-96 ">
                        {availablePPPGs.map((ppg: any, index: number) => (
                            <div
                                key={ppg.value}
                                className={`font-medium p-2.5 text-greyFont w-full font-xs  border-borderLinePurpuleAlt ${
                                    availablePPPGs.length - 1 !== index ? ' border-b' : ''
                                }`}
                            >
                                <Checkbox value={ppg.value} checked={tempCheckedValues.includes(ppg.value)}>
                                    {ppg.label}
                                </Checkbox>
                            </div>
                        ))}
                    </div>
                </Checkbox.Group>
            </div>
            {tempCheckedValues.length > 0 && (
                <div className="h-18 w-full bg-bgBackground p-2 rounded mt-2 overflow-y-scroll flex flex-wrap">
                    {availablePPPGs
                        .filter((item: any) => tempCheckedValues.includes(item.value))
                        .map((item: any) => (
                            <div
                                key={item.value}
                                className="px-2 text-white mr-2 bg-purpleFont rounded h-6 font-xxs flex justify-center items-center mb-2"
                            >
                                <span className="mr-2">{item.label}</span>
                                <button type="button" onClick={() => handleRemoveSelection(item.value)}>
                                    <Cross stroke="white" />
                                </button>
                            </div>
                        ))}
                </div>
            )}

            <div className="flex justify-end pt-6 font-bold">
                <button
                    type="button"
                    className="mr-2 px-6 py-1 rounded cursor-pointer text-purpleFont bg-white border border-purpleFont font-xxs "
                    onClick={onResetClick}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="text-white  px-6 py-1 rounded cursor-pointer  bg-purpleFont border border-purpleFont font-xxs"
                    onClick={onApplyChange}
                >
                    Apply
                </button>
            </div>
        </StyledModal>
    );
};

export default LinkScenarioModal;

const StyledModal = styled(Modal)`
    .ant-modal-title {
        ${tw`text-subHeaderColor`}
    }

    .ant-modal-close-x span svg {
        fill: '#140B1D';
    }

    .ant-modal-header {
        border: 0px;
    }

    .ant-modal {
        ${tw`w-1/3`}
    }

    .ant-modal-body {
        ${tw`pt-0`}
    }

    .ant-checkbox-checked .ant-checkbox-inner {
        ${tw`bg-purpleFont border-purpleFont`}
    }

    .ant-checkbox-wrapper {
        font-size: 11px;
    }

    .ant-checkbox-inner {
        height: 12px;
        width: 12px;
    }
`;

const LevelsFilterDiv = styled.div`
    ${tw`flex gap-2 items-center pt-4 pb-2`}
`;
